import * as React from 'react';
import { createPortal } from 'react-dom';
import { AppUser } from 'Src/views/App/App';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING } from '../../consts/user';
import { Role } from '../../models/roles';
import { getUserPermissions } from '../../helpers/user/user';
import { Permission } from '../../models/permission';
import { KIND } from '../../consts/school';
import { Avatar } from '../Avatar/Avatar';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import './PermissionSelector.scss';

interface State {
  isOpen: boolean;
  search: string;
  selectedPermissionId?: string;
}

interface Props {
  user: AppUser;
  history: any;
  onLogoutClick: (event) => void;
  onRoleChange: (
    roleName: string,
    permissionSchoolId: string,
    permissionSchoolName: string,
    permission: Permission
  ) => any;
  availableRoles: Role[];
}

const ROLE_META = {
  ADMIN: { label: 'Admin', color: '#227ebd', tint: '#e9f1fa' },
  MANAGER: { label: 'Manager', color: '#7b5bd6', tint: '#efeafb' },
  TEACHER: { label: 'Teacher', color: '#2f9e5a', tint: '#e7f3ec' },
  COACH: { label: 'Coach', color: '#f08c1a', tint: '#fdf0dd' },
  PARENT: { label: 'Parent', color: '#0f8a8a', tint: '#e3f2f2' },
  STUDENT: { label: 'Student', color: '#d64550', tint: '#fbe9ea' },
  GOVERNOR: { label: 'Trustee/Governor', color: '#5b6b7b', tint: '#eef2f7' },
  TOURNAMENT_FOLLOWER: { label: 'Tournament follower', color: '#5b6b7b', tint: '#eef2f7' }
};

const CREST_COLORS = [
  '#12386b',
  '#227ebd',
  '#2f9e5a',
  '#0f7a5a',
  '#0f8a8a',
  '#7b5bd6',
  '#c94fa0',
  '#a01d2e',
  '#c85a1e',
  '#5b6b7b'
];

const ICON = {
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16M21 21l-4.3-4.3',
  chevron: 'M9 18l6-6-6-6',
  check: 'M20 6 9 17l-5-5',
  logout: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  trophy: 'M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4zM5 4H3v2a3 3 0 0 0 3 3M19 4h2v2a3 3 0 0 1-3 3',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  chevronDown: 'M6 9l6 6 6-6',
  switch: 'M16 3l4 4-4 4M20 7H8M8 21l-4-4 4-4M4 17h12'
};

const Icon: React.FC<{ path: string; size?: number; strokeWidth?: number }> = ({
  path,
  size = 16,
  strokeWidth = 1.8
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={path} />
  </svg>
);

export class PermissionSelector extends React.Component<Props, State> {
  private triggerRef = React.createRef<HTMLButtonElement>();

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      search: ''
    };
  }

  onSelectorClick = event => {
    event.preventDefault();
    this.setState(({ isOpen }) => ({ isOpen: !isOpen }));
  };

  onClose = () => this.setState({ isOpen: false, search: '', selectedPermissionId: undefined });

  componentDidMount() {
    window.addEventListener('resize', this.onClose);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.onClose);
  }

  onRoleClick = (event, permission: Permission): void => {
    event.preventDefault();
    const roleName = permission.preset;
    const permissionSchoolId = permission.school.id;
    const permissionSchoolName = permission.school.name;

    const history = this.props.history;

    this.setState({ selectedPermissionId: permission.id });

    this.props.onRoleChange(roleName, permissionSchoolId, permissionSchoolName, permission).then(() => {
      this.onClose();
      const isParent = roleName === ROLE.PARENT;
      const isCoach = roleName === ROLE.COACH;
      const isTeacher = roleName === ROLE.TEACHER;
      const isStudent = roleName === ROLE.STUDENT;
      const isManager = roleName === ROLE.MANAGER;
      const isAdmin = roleName === ROLE.ADMIN;
      const isSchoolUnion = permission.school.kind === KIND.SCHOOL_UNION;
      const isSchoolUnionAdmin = isAdmin && isSchoolUnion;
      const isSchoolAdmin = isAdmin && !isSchoolUnion;

      switch (true) {
        case isSchoolUnionAdmin:
          history.push({ pathname: '/tournaments/upcoming' });
          break;
        case isSchoolAdmin:
          history.push({ pathname: '/dashboard' });
          break;
        case isParent || isStudent || isTeacher || isCoach || isManager:
          history.push({ pathname: '/calendar' });
          break;
        default:
          history.push({ pathname: '/calendar' });
      }
    });
  };

  onProfileClick = () => {
    this.onClose();
    this.props.history.push({ pathname: '/profile/summary' });
  };

  onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ search: event.target.value });

  getRoleMeta = (role?: string) =>
    ROLE_META[role || ''] || {
      label: ROLE_SERVER_TO_CLIENT_MAPPING[role || ''] || role || 'Role',
      color: '#5b6b7b',
      tint: '#eef2f7'
    };

  crestColor(name: string) {
    let value = 0;
    for (let index = 0; index < name.length; index++) value = (value * 31 + name.charCodeAt(index)) >>> 0;
    return CREST_COLORS[value % CREST_COLORS.length];
  }

  initials(name: string) {
    const words = name
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return `${(words[0] || '')[0] || ''}${(words[1] || '')[0] || (words[0] || '')[1] || ''}`.toUpperCase() || '?';
  }

  renderCrest(permission: Permission | { preset: string; school?: { name?: string; kind?: string } }, size = 34) {
    const schoolName = permission.school && permission.school.name;
    const isUnion = permission.school && permission.school.kind === KIND.SCHOOL_UNION;
    const meta = this.getRoleMeta(permission.preset);

    if (isUnion) {
      return (
        <span
          className="sitRoleMenuCrest sitRoleMenuCrest--icon"
          style={{
            width: size,
            height: size,
            borderRadius: Math.round(size / 3.4),
            backgroundColor: '#fbf1dc',
            color: '#b0821f'
          }}
        >
          <Icon path={ICON.trophy} size={Math.round(size * 0.5)} />
        </span>
      );
    }

    if (!schoolName || permission.preset === ROLE.PARENT || permission.preset === ROLE.STUDENT) {
      return (
        <span
          className="sitRoleMenuCrest sitRoleMenuCrest--icon"
          style={{
            width: size,
            height: size,
            borderRadius: Math.round(size / 3.4),
            backgroundColor: meta.tint,
            color: meta.color
          }}
        >
          <Icon path={ICON.user} size={Math.round(size * 0.5)} />
        </span>
      );
    }

    const color = this.crestColor(schoolName);
    return (
      <span
        className="sitRoleMenuCrest"
        style={{
          width: size,
          height: size,
          borderRadius: Math.round(size / 3.4),
          background: `linear-gradient(135deg, ${color}, ${color}cc)`
        }}
      >
        {this.initials(schoolName)}
      </span>
    );
  }

  renderRoleBadge(role: string, small = false) {
    const meta = this.getRoleMeta(role);
    return (
      <span
        className={small ? 'sitRoleBadge sitRoleBadge--sm' : 'sitRoleBadge'}
        style={{ backgroundColor: meta.tint, color: meta.color }}
      >
        <i style={{ backgroundColor: meta.color }} />
        {meta.label}
      </span>
    );
  }

  getFilteredPermissions(searchValue: string = this.state.search) {
    const activePermission = this.props.user.activePermission as Permission & { _id?: string };
    const activePermissionId = activePermission && String(activePermission.id || activePermission._id || '');
    const permissions = getUserPermissions(this.props.availableRoles).filter(permission => {
      if (activePermissionId && String(permission.id) === activePermissionId) return false;
      if (
        activePermission &&
        permission.preset === activePermission.preset &&
        (permission.preset === ROLE.PARENT || permission.preset === ROLE.STUDENT)
      ) {
        return false;
      }
      switch (permission.preset) {
        case ROLE.STUDENT:
        case ROLE.TOURNAMENT_FOLLOWER:
        case ROLE.GOVERNOR:
          return false;
        default:
          return true;
      }
    });

    const search = searchValue.trim().toLowerCase();
    const filtered = search
      ? permissions.filter(permission => {
          const roleLabel = this.getRoleMeta(permission.preset).label.toLowerCase();
          const schoolName = (permission.school && permission.school.name ? permission.school.name : '').toLowerCase();
          return roleLabel.indexOf(search) !== -1 || schoolName.indexOf(search) !== -1;
        })
      : permissions;

    return filtered.sort((a, b) => {
      const aLabel = a.school && a.school.name ? a.school.name : this.getRoleMeta(a.preset).label;
      const bLabel = b.school && b.school.name ? b.school.name : this.getRoleMeta(b.preset).label;
      return (
        aLabel.localeCompare(bLabel) || this.getRoleMeta(a.preset).label.localeCompare(this.getRoleMeta(b.preset).label)
      );
    });
  }

  renderRoleRow(permission: Permission) {
    const roleLabel = this.getRoleMeta(permission.preset).label;
    const isParentOrStudent = permission.preset === ROLE.PARENT || permission.preset === ROLE.STUDENT;
    const primary = isParentOrStudent ? roleLabel : permission.school.name;
    const subtitle =
      permission.school.kind === KIND.SCHOOL_UNION
        ? 'School Union Account'
        : isParentOrStudent
        ? 'Personal account'
        : '';
    const isSelected = this.state.selectedPermissionId === permission.id;

    return (
      <button
        key={permission.id}
        type="button"
        className={isSelected ? 'sitRoleMenuRow sitRoleMenuRow--selected' : 'sitRoleMenuRow'}
        onClick={event => this.onRoleClick(event, permission)}
      >
        {this.renderCrest(permission, 34)}
        <span className="sitRoleMenuRow__main">
          <strong>{primary}</strong>
          {subtitle && <em>{subtitle}</em>}
        </span>
        {!isParentOrStudent && this.renderRoleBadge(permission.preset, true)}
        {isSelected ? (
          <span className="sitRoleMenuRow__check">
            <Icon path={ICON.check} size={17} strokeWidth={2.6} />
          </span>
        ) : (
          <span className="sitRoleMenuRow__go">
            <Icon path={ICON.chevron} size={15} />
          </span>
        )}
      </button>
    );
  }

  render() {
    const { user } = this.props;
    const { isOpen, search } = this.state;
    const { activeSchoolName, activeRole, profile } = user;
    const userRoleMapped = ROLE_SERVER_TO_CLIENT_MAPPING[activeRole] || activeRole || 'Role';
    const isParent = activeRole === ROLE.PARENT;
    const isStudent = activeRole === ROLE.STUDENT;
    const activeTitle = isParent || isStudent ? userRoleMapped : activeSchoolName || 'School';
    const userFullName = getFullName(profile) || 'User';
    const userEmail = (profile && ((profile as any).email || (profile as any).emailAddress)) || '';
    const permissions = this.getFilteredPermissions();
    const availablePermissionCount = this.getFilteredPermissions('').length;
    const triggerRect = this.triggerRef.current ? this.triggerRef.current.getBoundingClientRect() : undefined;
    const panelStyle: React.CSSProperties =
      window.innerWidth <= 767
        ? { top: 60, right: 8, left: 8, width: 'auto' }
        : triggerRect
        ? {
            top: triggerRect.bottom + 8,
            right: Math.max(8, window.innerWidth - triggerRect.right)
          }
        : { top: 68, right: 12 };

    return (
      <div className={isOpen ? 'sitRoleMenuRoot sitRoleMenuRoot--open' : 'sitRoleMenuRoot'}>
        <button
          ref={this.triggerRef}
          type="button"
          className="sitRoleMenuTrigger"
          onClick={this.onSelectorClick}
          aria-label="Open account menu"
          aria-expanded={isOpen}
        >
          <Avatar user={this.props.user} history={this.props.history} onClick={() => undefined} />
          <span className="sitRoleMenuTrigger__chevron">
            <Icon path={ICON.chevronDown} size={15} />
          </span>
        </button>

        {isOpen &&
          createPortal(
            <>
              <button
                type="button"
                className="bDropdownOverlay sitRoleMenuOverlay"
                onClick={this.onClose}
                aria-label="Close account menu"
              />
              <div className="sitRoleMenuPanel" role="menu" style={panelStyle}>
                <div className="sitRoleMenuPerson">
                  <Avatar user={this.props.user} history={this.props.history} onClick={() => undefined} />
                  <div>
                    <strong>{userFullName}</strong>
                    {userEmail && <span>{userEmail}</span>}
                  </div>
                </div>

                <div className="sitRoleMenuActive">
                  <div className="sitRoleMenuActive__top">
                    <span>Signed in as</span>
                    {this.renderRoleBadge(activeRole, true)}
                  </div>
                  <div className="sitRoleMenuActive__role">
                    {this.renderCrest(
                      {
                        preset: activeRole,
                        school: activeSchoolName
                          ? { name: activeSchoolName, kind: user.activeSchool && user.activeSchool.kind }
                          : undefined
                      },
                      34
                    )}
                    <strong>{activeTitle}</strong>
                  </div>
                  <button type="button" onClick={this.onProfileClick}>
                    <Icon path={ICON.user} size={15} />
                    View profile
                  </button>
                </div>

                {availablePermissionCount > 0 && (
                  <>
                    <div className="sitRoleMenuSwitchHead">
                      <Icon path={ICON.switch} size={13} />
                      Switch role
                      <span>{availablePermissionCount} available</span>
                    </div>

                    <div className="sitRoleMenuSearch">
                      <Icon path={ICON.search} size={15} />
                      <input value={search} placeholder="Search school or role..." onChange={this.onSearchChange} />
                    </div>

                    <div className="sitRoleMenuList">
                      {permissions.length > 0 ? (
                        permissions.map(permission => this.renderRoleRow(permission))
                      ) : (
                        <div className="sitRoleMenuEmpty">Nothing matches “{search}”.</div>
                      )}
                    </div>
                  </>
                )}

                <div className="sitRoleMenuFooter">
                  <button type="button" onClick={this.props.onLogoutClick}>
                    <Icon path={ICON.logout} size={16} />
                    Log out
                  </button>
                </div>
              </div>
            </>,
            document.body
          )}
      </div>
    );
  }
}
